import { Timestamp } from 'firebase/firestore';
import { UserCredential } from 'firebase/auth';

export type Role = 'user' | 'organizer' | 'admin';

export interface AuthError {
  code: keyof typeof AUTH_ERROR_MESSAGES;
  message: string;
  details: any;
}

export type UserType = 'user' | 'organizer' | 'admin';

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null | undefined;
  emailVerified: boolean;
  userType: UserType;
  roles: string[];
  lastLoginAt: any;
  updatedAt: any;
  createdAt?: any;
  isOrganizer?: boolean;
  preferences: {
    notifications: boolean;
    emailMarketing: boolean;
    theme: 'light' | 'dark' | 'system';
  };
  stats: {
    eventsAttended: number;
    eventsCreated: number;
    ticketsPurchased: number;
    totalRevenue: number;
    activeEvents: number;
    totalBookings: number;
  };
  organizationName?: string;
  organizationDescription?: string;
  organizationLogo?: string;
}

export interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  error: AuthError | null;
}

export interface SignInCredentials {
  email: string;
  password: string;
}

export interface SignUpCredentials extends SignInCredentials {
  displayName: string;
  userType: UserType;
}

export interface AuthProvider {
  id: string;
  name: string;
  icon?: string;
}

export const AUTH_ERROR_CODES = {
  POPUP_CLOSED: 'auth/popup-closed-by-user',
  POPUP_BLOCKED: 'auth/popup-blocked',
  EMAIL_EXISTS: 'auth/email-already-in-use',
  INVALID_EMAIL: 'auth/invalid-email',
  WEAK_PASSWORD: 'auth/weak-password',
  WRONG_PASSWORD: 'auth/wrong-password',
  USER_NOT_FOUND: 'auth/user-not-found',
  NETWORK_ERROR: 'auth/network-error',
  TOO_MANY_REQUESTS: 'auth/too-many-requests',
  REQUIRES_RECENT_LOGIN: 'auth/requires-recent-login'
} as const;

export const AUTH_ERROR_MESSAGES = {
  [AUTH_ERROR_CODES.POPUP_CLOSED]: 'Sign-in was cancelled. Please try again.',
  [AUTH_ERROR_CODES.POPUP_BLOCKED]: 'Pop-up was blocked by your browser. Please allow pop-ups and try again.',
  [AUTH_ERROR_CODES.EMAIL_EXISTS]: 'This email is already registered. Please sign in instead.',
  [AUTH_ERROR_CODES.INVALID_EMAIL]: 'Invalid email address. Please check and try again.',
  [AUTH_ERROR_CODES.WEAK_PASSWORD]: 'Password is too weak. Please use a stronger password.',
  [AUTH_ERROR_CODES.WRONG_PASSWORD]: 'Incorrect password. Please try again.',
  [AUTH_ERROR_CODES.USER_NOT_FOUND]: 'No account found with this email. Please sign up first.',
  [AUTH_ERROR_CODES.NETWORK_ERROR]: 'Network error. Please check your internet connection.',
  [AUTH_ERROR_CODES.TOO_MANY_REQUESTS]: 'Too many attempts. Please try again later.',
  [AUTH_ERROR_CODES.REQUIRES_RECENT_LOGIN]: 'This action requires recent authentication. Please sign in again.'
} as const;

export interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  role: UserType;
  createdAt: Date;
  lastLoginAt: Date;
  preferences: {
    notifications: boolean;
    emailMarketing: boolean;
    theme: 'light' | 'dark' | 'system';
  };
  stats: {
    eventsAttended: number;
    eventsCreated: number;
    ticketsPurchased: number;
    totalRevenue: number;
    activeEvents: number;
  };
  organizationName?: string;
  organizationDescription?: string;
  organizationLogo?: string;
}

export interface AdminProfile extends UserProfile {
  role: 'admin';
  permissions: string[];
}

export type UserProfileType = UserProfile | AdminProfile;

export interface AuthContextType {
  user: AuthUser | null;
  loading: boolean;
  error: AuthError | null;
  signInWithGoogle: () => Promise<AuthUser>;
  signInWithEmail: (credentials: { email: string; password: string }) => Promise<AuthUser>;
  signUpWithEmail: (
    email: string,
    password: string,
    name: string,
    userType: UserType,
    additionalData?: Record<string, any>
  ) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  hasRole: (role: string) => boolean;
  ensureUserRoles: (userId: string) => Promise<void>;
  updateUserPreferences: (userId: string, preferences: Partial<AuthUser['preferences']>) => Promise<void>;
  updateUserStats: (userId: string, stats: Partial<AuthUser['stats']>) => Promise<void>;
  login: (email: string, password: string) => Promise<UserCredential>;
} 